import api from "../axios/axiosInstance";

/**
 * Get all contacts or leads
 * @param {Object} params - Query parameters
 * @param {boolean} params.isLead - false for contacts, true for leads
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search query
 * @param {string|Array} params.tag - Tag filter
 * @param {boolean} params.isFavourite - Filter favourites
 * @param {string|Array} params.status - Status filter (single status or array of statuses)
 * @param {boolean} params.sorted - Sort alphabetically
 * @param {string} params.category - Category ("contact" or "lead"), defaults to "contact"
 */
export const getAllContactsOrLeads = async (params) => {
  try {
    const paramsWithCategory = {
      ...params,
      category: params.category || "contact",
    };
    const response = await api.post(
      "/getAllContactsOrLeads",
      paramsWithCategory
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching contacts/leads:", error);
    throw error;
  }
};

/**
 * Get a single contact or lead by ID
 * @param {string} contact_id - The contact ID
 * @param {string} category - Category ("contact" or "lead")
 */
export const getSingleContactOrLead = async (
  contact_id,
  category = "contact"
) => {
  try {
    const response = await api.post("/getAllContactsOrLeads/single", {
      contact_id,
      category,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching single contact/lead:", error);
    throw error;
  }
};

/**
 * Add or edit a contact/lead
 * @param {FormData} formData - Form data containing contact information
 * FormData should include:
 * - contact_id (optional, for updates)
 * - firstname
 * - lastname
 * - company
 * - designation
 * - linkedin, instagram, telegram, twitter, facebook
 * - emailAddresses (JSON string array)
 * - phoneNumbers (JSON string array of {countryCode, number})
 * - contactImage (file, optional)
 * - isFavourite (boolean)
 * - notes
 * - website
 * - isLead (boolean)
 * - category (optional, defaults to "contact")
 */
export const addEditContactOrLead = async (formData) => {
  try {
    // Ensure category is set if not already present
    if (!formData.has("category")) {
      formData.append("category", "contact");
    }
    const response = await api.post("/addEditContactLeads", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error adding/editing contact/lead:", error);
    throw error;
  }
};

/**
 * Change status of a contact
 * If status is changed to "interested", contact will be converted to lead
 * @param {Object} params
 * @param {string} params.contact_id - Contact ID
 * @param {string} params.newStatus - New status value
 * @param {string} params.note - Optional note
 * @param {string} params.category - Category ("contact" or "lead"), defaults to "contact"
 */
export const changeContactStatus = async (params) => {
  try {
    const paramsWithCategory = {
      ...params,
      category: params.category || "contact",
    };
    const response = await api.put(
      "/contactAndLeadStatusPipline/status",
      paramsWithCategory
    );
    return response.data;
  } catch (error) {
    console.error("Error changing contact status:", error);
    throw error;
  }
};

/**
 * Get pipeline for a specific lead
 * @param {string} lead_id - Lead ID
 * @param {string} category - Category, defaults to "lead"
 */
export const getLeadPipeline = async (lead_id, category = "lead") => {
  try {
    const response = await api.post("/contactAndLeadStatusPipline/pipeline", {
      lead_id,
      category,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching lead pipeline:", error);
    throw error;
  }
};

/**
 * Get pipeline overview (all leads grouped by status)
 * @param {string} category - Category, defaults to "lead"
 */
export const getPipelineOverview = async (category = "lead") => {
  try {
    const response = await api.post("/contactAndLeadStatusPipline/overview", {
      category,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching pipeline overview:", error);
    throw error;
  }
};

/**
 * Delete a contact or lead
 * @param {string} contact_id - The contact ID to delete
 * @param {string} category - Category ("contact" or "lead")
 */
export const deleteContactOrLead = async (contact_id, category = "contact") => {
  try {
    const response = await api.post("/addEditContactLeads/delete", {
      contact_id,
      category,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting contact/lead:", error);
    throw error;
  }
};

/**
 * Toggle favorite status of a contact
 * @param {string} contact_id - The contact ID
 * @param {boolean} isFavourite - The new favorite status
 * @param {string} category - Category ("contact" or "lead")
 */
export const toggleContactFavorite = async (
  contact_id,
  isFavourite,
  category = "contact"
) => {
  try {
    const response = await api.put("/addEditContactLeads/toggle-favorite", {
      contact_id,
      isFavourite,
      category,
    });
    return response.data;
  } catch (error) {
    console.error("Error toggling favorite:", error);
    throw error;
  }
};

/**
 * Batch delete multiple contacts
 * @param {Array<string>} contact_ids - Array of contact IDs to delete
 * @param {string} category - Category ("contact" or "lead")
 */
export const batchDeleteContacts = async (
  contact_ids,
  category = "contact"
) => {
  try {
    const response = await api.post("/addEditContactLeads/batch-delete", {
      contact_ids,
      category,
    });
    return response.data;
  } catch (error) {
    console.error("Error batch deleting contacts:", error);
    throw error;
  }
};

/**
 * Update/assign tags to a contact
 * @param {Object} params
 * @param {string} params.contact_id - Contact ID
 * @param {Array} params.tags - Array of tag objects with tag_id, tag, emoji, order
 * @param {string} params.category - Category ("contact" or "lead"), defaults to "contact"
 */
export const updateContactTags = async (params) => {
  try {
    const paramsWithCategory = {
      ...params,
      category: params.category || "contact",
    };
    const response = await api.post(
      "/tag/assignedToContact",
      paramsWithCategory
    );
    return response.data;
  } catch (error) {
    console.error("Error updating contact tags:", error);
    throw error;
  }
};

/**
 * Get all tags for the current user
 */
export const getUserTags = async () => {
  try {
    const response = await api.get("/tag/getTagsOfUser");
    return response.data;
  } catch (error) {
    console.error("Error fetching user tags:", error);
    throw error;
  }
};

/**
 * Add a new tag for the user
 * @param {Object} params
 * @param {string} params.tag - Tag name
 * @param {string} params.emoji - Tag emoji
 */
export const addUserTag = async (params) => {
  try {
    const response = await api.post("/tag/addTagToUser", params);
    return response.data;
  } catch (error) {
    console.error("Error adding user tag:", error);
    throw error;
  }
};

/**
 * Update first phone number or email address of a contact
 * @param {Object} params
 * @param {string} params.contact_id - Contact ID
 * @param {string} params.field - Field to update ("phone" or "email")
 * @param {string} params.value - New value (phone number or email)
 * @param {string} params.category - Category ("contact" or "lead"), defaults to "contact"
 */
export const updateFirstPhoneOrEmail = async (params) => {
  try {
    const paramsWithCategory = {
      ...params,
      category: params.category || "contact",
    };
    const response = await api.put(
      "/addEditContactLeads/update-first-contact",
      paramsWithCategory
    );
    return response.data;
  } catch (error) {
    console.error("Error updating phone/email:", error);
    throw error;
  }
};

/**
 * Get pipeline (status change history) for a contact/lead
 * @param {string} lead_id - Contact/Lead ID
 * @returns {Promise} Array of pipeline items with currentStatus, ordered oldest to newest
 */
export const getContactPipeline = async (lead_id) => {
  try {
    const response = await api.post("/contactAndLeadStatusPipline/pipeline", {
      lead_id,
    });

    if (response.data && response.data.data) {
      // Return only the latest 6 items, sorted oldest to newest (left to right)
      const allPipeline = response.data.data;
      const latest6 = allPipeline.slice(-6); // Get last 6 items

      // Extract only currentStatus from each item
      return latest6.map((item) => ({
        status: item.currentStatus,
        createdAt: item.createdAt,
      }));
    }

    return [];
  } catch (error) {
    console.error("Error fetching contact pipeline:", error);
    return [];
  }
};

/**
 * Update attachments for a contact/lead
 * @param {string} contact_id - The contact ID
 * @param {Array} files - Array of File objects to upload
 * @param {Array} existingAttachments - Array of existing attachments to keep
 */
export const updateContactAttachments = async (
  contact_id,
  filesWithDescriptions = [],
  existingAttachments = []
) => {
  try {
    const formData = new FormData();
    formData.append("contact_id", contact_id);

    // Append existing attachments as JSON string
    formData.append("existingAttachments", JSON.stringify(existingAttachments));

    // Append new files with descriptions
    filesWithDescriptions.forEach((item, index) => {
      formData.append("attachments", item.file);
      formData.append(`description_${index}`, item.description || "");
    });

    const response = await api.put(
      "/addEditContactLeads/update-attachments",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating attachments:", error);
    throw error;
  }
};
